import { createSlice } from '@reduxjs/toolkit';
import { createAsyncActions } from '@icebreaker/shared/store';
import type {
  AdminCityEventCountDto,
  AdminEventAttendeeListDto,
  AdminEventAttendeeQuery,
  AdminEventAttendeeRowDto,
  AdminEventStatsListDto,
  AdminEventStatsQuery,
  AdminEventStatsRowDto,
  AdminEventsByCityDto,
  AdminPlatformStatsDto,
  ApiErrorBody,
} from '@icebreaker/shared';

/**
 * GENEL GÖRÜNÜM PANOSU DEPOSU (kullanıcı kararı 2026-09-02 §3).
 *
 * `admin.slice.ts`ten AYRI dosyadır ve bu bilinçlidir: pano dört yeni okuma
 * yüzeyi getirdi ve tek dosya `file-size-ceiling.guard` tavanını (500) aştı.
 * Bölme kuyruk/kullanıcı durumunu HİÇ kıpırdatmaz — kök indirgeyicide ayrı bir
 * dal (`adminStats`) olarak durur.
 */

// --- Async action üçlüleri ---------------------------------------------------

/**
 * Dördü de OKUMADIR; hiçbiri sunucu durumunu değiştirmez, bu yüzden
 * `INVALIDATION_MAP`te değil `READ_ONLY_PREFIXES` listesindedirler.
 */
export const fetchPlatformStats = createAsyncActions<void, AdminPlatformStatsDto>(
  'admin/statsOverviewFetch',
);
export const fetchEventsByCity = createAsyncActions<void, AdminEventsByCityDto>(
  'admin/statsCitiesFetch',
);
export const fetchEventStats = createAsyncActions<AdminEventStatsQuery, AdminEventStatsListDto>(
  'admin/statsEventsFetch',
);
export const fetchEventAttendees = createAsyncActions<
  { eventId: string; query: AdminEventAttendeeQuery },
  AdminEventAttendeeListDto
>('admin/statsAttendeesFetch');

// --- Durum -------------------------------------------------------------------

type Phase = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface AdminStatsState {
  platformStats: {
    data: AdminPlatformStatsDto | null;
    phase: Phase;
    error: ApiErrorBody | null;
  };
  cities: {
    items: AdminCityEventCountDto[];
    locatedTotal: number;
    onlineCount: number;
    phase: Phase;
    error: ApiErrorBody | null;
  };
  events: {
    items: AdminEventStatsRowDto[];
    page: number;
    total: number;
    hasNext: boolean;
    status: AdminEventStatsQuery['status'];
    phase: Phase;
    error: ApiErrorBody | null;
  };
  /** Katman TEK etkinlik için açılır; kapanınca kimlik ve satırlar sıfırlanır. */
  attendees: {
    eventId: string | null;
    eventTitle: string;
    items: AdminEventAttendeeRowDto[];
    page: number;
    total: number;
    hasNext: boolean;
    checkedInTotal: number;
    phase: Phase;
    error: ApiErrorBody | null;
  };
}

const initialState: AdminStatsState = {
  platformStats: { data: null, phase: 'idle', error: null },
  cities: { items: [], locatedTotal: 0, onlineCount: 0, phase: 'idle', error: null },
  events: {
    items: [],
    page: 1,
    total: 0,
    hasNext: false,
    status: 'all',
    phase: 'idle',
    error: null,
  },
  attendees: {
    eventId: null,
    eventTitle: '',
    items: [],
    page: 1,
    total: 0,
    hasNext: false,
    checkedInTotal: 0,
    phase: 'idle',
    error: null,
  },
};

const slice = createSlice({
  name: 'adminStats',
  initialState,
  reducers: {
    /** Katılımcı katmanı kapandı — kimlik ve satırlar düşer (bayat liste kalmaz). */
    attendeeLayerClosed(state) {
      state.attendees = initialState.attendees;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlatformStats.request, (state) => {
        state.platformStats.phase = 'loading';
        state.platformStats.error = null;
      })
      .addCase(fetchPlatformStats.success, (state, action) => {
        state.platformStats.phase = 'succeeded';
        state.platformStats.data = { ...action.payload };
      })
      .addCase(fetchPlatformStats.failure, (state, action) => {
        state.platformStats.phase = 'failed';
        state.platformStats.error = action.payload;
      })
      .addCase(fetchEventsByCity.request, (state) => {
        state.cities.phase = 'loading';
        state.cities.error = null;
      })
      .addCase(fetchEventsByCity.success, (state, action) => {
        state.cities.phase = 'succeeded';
        state.cities.items = action.payload.cities.map((city) => ({ ...city }));
        state.cities.locatedTotal = action.payload.locatedTotal;
        state.cities.onlineCount = action.payload.onlineCount;
      })
      .addCase(fetchEventsByCity.failure, (state, action) => {
        state.cities.phase = 'failed';
        state.cities.error = action.payload;
      })
      .addCase(fetchEventStats.request, (state, action) => {
        state.events.phase = 'loading';
        state.events.error = null;
        state.events.status = action.payload.status;
      })
      .addCase(fetchEventStats.success, (state, action) => {
        const data = action.payload;
        state.events.phase = 'succeeded';
        state.events.items = data.items.map((item) => ({ ...item }));
        state.events.page = data.page;
        state.events.total = data.total;
        state.events.hasNext = data.hasNext;
      })
      .addCase(fetchEventStats.failure, (state, action) => {
        state.events.phase = 'failed';
        state.events.error = action.payload;
      })
      .addCase(fetchEventAttendees.request, (state, action) => {
        const sameEvent = state.attendees.eventId === action.payload.eventId;
        state.attendees.eventId = action.payload.eventId;
        state.attendees.phase = 'loading';
        state.attendees.error = null;
        if (!sameEvent) {
          // Başka bir etkinliğe geçildi: önceki dökümün satırları GÖRÜNMEZ.
          state.attendees.items = [];
          state.attendees.eventTitle = '';
          state.attendees.total = 0;
          state.attendees.checkedInTotal = 0;
        }
      })
      .addCase(fetchEventAttendees.success, (state, action) => {
        const data = action.payload;
        state.attendees.phase = 'succeeded';
        state.attendees.items = data.items.map((item) => ({ ...item }));
        state.attendees.eventTitle = data.eventTitle;
        state.attendees.page = data.page;
        state.attendees.total = data.total;
        state.attendees.hasNext = data.hasNext;
        state.attendees.checkedInTotal = data.checkedInTotal;
      })
      .addCase(fetchEventAttendees.failure, (state, action) => {
        state.attendees.phase = 'failed';
        state.attendees.error = action.payload;
      });
  },
});

export const { attendeeLayerClosed } = slice.actions;
export const adminStatsReducer = slice.reducer;
